/**
 * Modules of K-Management and who may use them (docs/adr/ADR-005, ADR-008).
 *
 * What a user may do in a module comes from three things:
 * 1. the **level**: none, read, write or admin (bulk, irreversible or
 *    configuration operations of the module);
 * 2. for the modules with data of their own, the **data scope**: every record,
 *    or only the records the user entered (a sales user sees their own
 *    customers and quotes);
 * 3. the **features** that can be hidden or granted, each needing a level:
 *    granted by default ("di default tutto") or reserved.
 *
 * Administrators assign a **profile** (ruolo-modello: e.g. Commerciale), and
 * can set exceptions for a single user, which win over the profile. System
 * administrators (`isAdmin`) have everything.
 *
 * The catalogue lives in code, not in the database: a permission only means
 * something if the API checks it, so adding one is always a code change.
 * Adding a module: append it here, then add its API module guarded with
 * @RequirePermissions() and its pages in the web app. Adding a feature: append
 * it to its module and check its key in the API.
 */
import { z } from 'zod';
/** Level of access to a module, lowest first. */
export const ACCESS_LEVELS = ['none', 'read', 'write', 'admin'];
export const ACCESS_LEVEL_LABELS = {
    none: 'Nessuno',
    read: 'Consultazione',
    write: 'Modifica',
    admin: 'Amministrazione',
};
export const ACCESS_LEVEL_DESCRIPTIONS = {
    none: 'Il modulo non compare.',
    read: 'Vede ed esporta.',
    write: 'Inserisce e modifica.',
    admin: 'Anche operazioni massive, irreversibili o di configurazione del modulo.',
};
const LEVEL_RANK = { none: 0, read: 1, write: 2, admin: 3 };
export function levelAtLeast(level, minimum) {
    return LEVEL_RANK[level] >= LEVEL_RANK[minimum];
}
/** Which records of a module with data of its own a user sees. */
export const DATA_SCOPES = ['all', 'own'];
/** When neither the profile nor the user says otherwise: the safest choice. */
export const DEFAULT_DATA_SCOPE = 'own';
export const APP_MODULES = [
    {
        key: 'anagrafica',
        label: 'Anagrafica',
        description: 'Clienti, fornitori e gli altri soggetti, condivisi da tutti i moduli.',
        path: '/anagrafica',
        dataScope: { all: 'Tutte le anagrafiche', own: 'Solo quelle inserite da lui' },
        features: [
            {
                key: 'anagrafica.documents',
                label: 'Documenti',
                description: 'Vede e scarica i file dei soggetti su SharePoint; con la modifica li carica ed elimina. Non concessa di base.',
                level: 'read',
                defaultGranted: false,
            },
            {
                key: 'anagrafica.suppliers',
                label: 'Fornitori',
                description: 'Vede i fornitori; senza, vede solo gli altri ruoli.',
                level: 'read',
                defaultGranted: true,
            },
            {
                key: 'anagrafica.reserved-roles',
                label: 'Dipendenti e ruoli riservati',
                description: 'Vede i soggetti e le schede dei ruoli riservati, come i dipendenti.',
                level: 'read',
                defaultGranted: false,
            },
            {
                key: 'anagrafica.payment-terms',
                label: 'Condizioni di pagamento e priorità',
                description: 'Vede termini e metodi di pagamento, priorità e conti dei soggetti.',
                level: 'read',
                defaultGranted: true,
            },
            {
                key: 'anagrafica.bank-details',
                label: 'Dati bancari (IBAN)',
                description: 'Vede gli IBAN dei soggetti.',
                level: 'read',
                defaultGranted: true,
            },
            {
                key: 'anagrafica.price-lists',
                label: 'Assegnare i listini ai clienti',
                description: 'Sceglie il listino attività e il listino passività di un cliente. Il listino passività richiede anche «Costi e margini». Non concessa di base: per amministratori e supervisori.',
                level: 'write',
                defaultGranted: false,
            },
            {
                key: 'anagrafica.export',
                label: 'Esporta elenco',
                description: 'Scarica l’elenco dei soggetti in CSV.',
                level: 'read',
                defaultGranted: true,
            },
        ],
    },
    {
        key: 'preventivatore',
        label: 'Preventivatore',
        description: 'Preventivi interni con costi e ricavi; approvati diventano offerte per il cliente.',
        path: '/preventivatore',
        dataScope: { all: 'Tutti i preventivi', own: 'Solo quelli creati da lui' },
        features: [
            {
                key: 'preventivatore.discounts',
                label: 'Sconti',
                description: 'Applica sconti alle righe dei preventivi.',
                level: 'write',
                defaultGranted: true,
            },
            {
                key: 'preventivatore.costs',
                label: 'Costi e margini',
                description: 'Vede e sceglie i costi previsti delle righe e il margine del preventivo. Dati riservati: non concessa di base.',
                level: 'read',
                defaultGranted: false,
            },
            {
                key: 'preventivatore.documents',
                label: 'Documenti delle offerte',
                description: 'Vede e scarica i file delle offerte su SharePoint (offerte firmate, e-mail, documenti del cliente); con la modifica li carica ed elimina. Concessa di base al profilo Commerciale.',
                level: 'read',
                defaultGranted: false,
            },
            {
                key: 'preventivatore.approve',
                label: 'Approvare i preventivi',
                description: 'Approva i preventivi di cui è il commerciale: diventano offerte, con il numero OFF e la versione.',
                level: 'write',
                defaultGranted: true,
            },
        ],
    },
    {
        key: 'commesse',
        label: 'Commesse e progetti',
        description: 'Le commesse con i clienti, nate dalle offerte accettate, e i progetti interni. Con la modifica si creano, si collegano le offerte, si nominano PM, Account e commerciale e si cambia lo stato; con l’amministrazione si annullano, si eliminano e si tolgono le offerte.',
        path: '/commesse',
        dataScope: {
            all: 'Tutte le commesse e i progetti',
            own: 'Solo quelli in cui è PM, Account o commerciale abilitato',
        },
        features: [
            {
                key: 'commesse.people-costs',
                label: 'Costi delle persone',
                description: 'Vede l’€/ora e il costo delle ore delle persone su commesse e progetti; senza, vede solo le ore. Non concessa di base: per il CFO.',
                level: 'read',
                defaultGranted: false,
            },
        ],
    },
    {
        key: 'tesoreria',
        label: 'Tesoreria',
        description: 'Fatture emesse e ricevute da Gestify, scadenze, incassi e pagamenti. Dati riservati: non è dato a nessun profilo di base.',
        path: '/tesoreria',
        dataScope: null,
        features: [
            {
                key: 'tesoreria.cards',
                label: 'Carte di credito',
                description: 'Vede le carte, i titolari e le spese con carta; con la modifica carica i file della banca. Non concessa di base.',
                level: 'read',
                defaultGranted: false,
            },
            {
                key: 'tesoreria.closures',
                label: 'Chiudere fatture senza movimento',
                description: 'Chiude il residuo di una fattura senza denaro (accordo transattivo, stralcio, rinuncia), con la motivazione e il documento, e annulla queste chiusure. Non concessa di base: per il CFO (incasso, attività 14).',
                level: 'write',
                defaultGranted: false,
            },
            {
                key: 'tesoreria.year_close',
                label: 'Chiudere l’anno',
                description: 'Chiude un anno della Tesoreria quando i controlli sono verdi o le differenze sono spiegate: da quel momento i suoi dati non si modificano più. Riaprirlo è solo dell’amministratore. Non concessa di base: per il CFO.',
                level: 'write',
                defaultGranted: false,
            },
            {
                key: 'tesoreria.schedule',
                label: 'Programmare le scadenze',
                description: 'Riprogramma le scadenze, cambia il conto, scrive le note e programma i giroconti dello Scadenzario. Non concessa di base: per il CFO.',
                level: 'write',
                defaultGranted: false,
            },
        ],
    },
    {
        key: 'organizzazione',
        label: 'Organizzazione',
        description: 'Ruoli aziendali, responsabilità e regole di K-Rev, in consultazione.',
        path: '/organizzazione',
        dataScope: null,
        features: [
            {
                key: 'organizzazione.roles',
                label: 'Modificare i ruoli aziendali',
                description: 'Aggiunge e modifica i ruoli aziendali: chi li ricopre, i sostituti e la gerarchia. Non concessa di base: per amministratori e supervisori.',
                level: 'read',
                defaultGranted: false,
            },
        ],
    },
    {
        key: 'persone',
        label: 'Persone',
        description: 'Organizzazione › Persone: i dipendenti, i loro dati, gli stipendi, gli acconti e i pagamenti. Dati personali riservati: non è dato a nessun profilo di base.',
        path: '/organizzazione/persone',
        dataScope: null,
        features: [
            {
                key: 'persone.costs',
                label: 'Vedere il costo del personale',
                description: 'Il costo aziendale di ogni persona e l’€/ora, dalle paghe del consulente; con la modifica di Persone carica anche le paghe del mese. Non concessa di base: per il CFO.',
                level: 'read',
                defaultGranted: false,
            },
        ],
    },
    {
        key: 'controllo',
        label: 'Controllo di gestione',
        description: 'I piani riclassificati del conto economico gestionale; più avanti il conto economico e il budget. Non è dato a nessun profilo di base.',
        path: '/controllo',
        dataScope: null,
        features: [
            {
                key: 'controllo.plans',
                label: 'Modificare i piani riclassificati',
                description: 'Crea, modifica, importa e duplica i piani riclassificati e le loro voci. Non concessa di base: per il CFO.',
                level: 'read',
                defaultGranted: false,
            },
        ],
    },
    {
        key: 'report',
        label: 'Report',
        description: 'Report di sintesi: per ora la Dashboard con fatturato, crediti e debiti. Dati riservati: non è dato a nessun profilo di base.',
        path: '/report',
        dataScope: null,
        features: [
            {
                key: 'report.dashboard',
                label: 'Dashboard',
                description: 'Fatturato del periodo, crediti da incassare e debiti da pagare per scadenza.',
                level: 'read',
                defaultGranted: true,
            },
        ],
    },
    {
        key: 'tabelle',
        label: 'Tabelle',
        description: 'Tabelle di riferimento dei moduli, con versioni e storico.',
        path: '/tabelle',
        dataScope: null,
        features: [],
    },
];
const MODULES = APP_MODULES;
export const MODULE_KEYS = APP_MODULES.map((module) => module.key);
export const FEATURE_KEYS = MODULES.flatMap((module) => module.features.map((feature) => feature.key));
const GRANTED_LEVELS = ACCESS_LEVELS.filter((level) => level !== 'none');
/** Every permission, in catalogue order: for each module its levels, then its features. */
export const PERMISSIONS = MODULES.flatMap((module) => [
    ...GRANTED_LEVELS.map((level) => `${module.key}.${level}`),
    ...module.features.map((feature) => feature.key),
]);
export function isModuleKey(value) {
    return MODULE_KEYS.includes(value);
}
export function isFeatureKey(value) {
    return FEATURE_KEYS.includes(value);
}
export function moduleDefinition(key) {
    return MODULES.find((module) => module.key === key);
}
export const NO_ACCESS = { modules: {}, featureOverrides: {} };
const moduleKeySchema = z.enum(MODULE_KEYS, { message: 'Modulo sconosciuto' });
export const accessSettingsSchema = z
    .object({
    modules: z.partialRecord(moduleKeySchema, z.object({
        level: z.enum(ACCESS_LEVELS, { message: 'Livello sconosciuto' }),
        scope: z.enum(DATA_SCOPES, { message: 'Visibilità sconosciuta' }).optional(),
    })),
    featureOverrides: z.partialRecord(z.string(), z.boolean()),
})
    .superRefine((settings, context) => {
    for (const key of Object.keys(settings.featureOverrides)) {
        if (!isFeatureKey(key)) {
            context.addIssue({
                code: 'custom',
                path: ['featureOverrides', key],
                message: 'Funzione sconosciuta',
            });
        }
    }
});
/**
 * Canonical form of the settings of a profile: only modules with access, a
 * scope only where the module has one, only features that differ from their
 * default, unknown keys (removed from the catalogue) dropped.
 */
export function normaliseProfileAccess(settings, catalogue = MODULES) {
    return normalise(settings, catalogue, 'profile');
}
/**
 * Canonical form of the exceptions of a user: like a profile, but `none` is
 * kept (it takes away a module of the profile) and every feature choice is
 * kept (it is relative to the profile, which can change).
 */
export function normaliseUserExceptions(settings, catalogue = MODULES) {
    return normalise(settings, catalogue, 'exceptions');
}
function normalise(settings, catalogue, kind) {
    const modules = {};
    const featureOverrides = {};
    for (const module of catalogue) {
        const access = settings.modules[module.key];
        if (access && (kind === 'exceptions' || access.level !== 'none')) {
            modules[module.key] =
                module.dataScope && access.scope && access.level !== 'none'
                    ? { level: access.level, scope: access.scope }
                    : { level: access.level };
        }
        for (const feature of module.features) {
            const override = settings.featureOverrides[feature.key];
            if (override !== undefined &&
                (kind === 'exceptions' || override !== feature.defaultGranted)) {
                featureOverrides[feature.key] = override;
            }
        }
    }
    return { modules, featureOverrides };
}
export function effectiveAccess(subject) {
    return effectiveAccessIn(MODULES, subject);
}
/** effectiveAccess() on any catalogue. The exceptions of the user win over the profile. */
export function effectiveAccessIn(catalogue, subject) {
    const permissions = [];
    const scopes = {};
    for (const module of catalogue) {
        const own = subject.exceptions.modules[module.key];
        const inherited = subject.profile?.modules[module.key];
        const level = subject.isAdmin
            ? 'admin'
            : (own?.level ?? inherited?.level ?? 'none');
        for (const granted of GRANTED_LEVELS) {
            if (levelAtLeast(level, granted))
                permissions.push(`${module.key}.${granted}`);
        }
        if (module.dataScope && level !== 'none') {
            scopes[module.key] = subject.isAdmin
                ? 'all'
                : (own?.scope ?? inherited?.scope ?? DEFAULT_DATA_SCOPE);
        }
        for (const feature of module.features) {
            const granted = subject.exceptions.featureOverrides[feature.key] ??
                subject.profile?.featureOverrides[feature.key] ??
                feature.defaultGranted;
            if (subject.isAdmin || (granted && levelAtLeast(level, feature.level))) {
                permissions.push(feature.key);
            }
        }
    }
    return { permissions, scopes };
}
export function hasPermissions(granted, required) {
    return required.every((permission) => granted.includes(permission));
}
/** Modules the user can open. */
export function modulesVisibleTo(granted) {
    return MODULES.filter((module) => granted.includes(`${module.key}.read`));
}
//# sourceMappingURL=permissions.js.map